import styles from "./shuffle-wrap.module.css";
import React, {
  HTMLAttributes,
  ReactNode,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { gsap } from "gsap";
import useFaultTextAnimation from "../use-fault-text-animation/use-fault-text-animation";
import { useGsap } from "../use-gsap/use-gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

/* eslint-disable-next-line */
export interface ShuffleWrapProps extends HTMLAttributes<HTMLElement> {
  // vertical || horizontal alignment
  orientation?: "vertical" | "horizontal";
  // Total number of slices for the inner images (clip paths)
  slicesTotal: number;
  // Animation values
  animation?: {
    duration?: number;
    ease?: string;
    delay?: number;
  };
  scrollTrigger?: ScrollTrigger.Vars;
  onComplete?: () => void;
  onReverseComplete?: () => void;
  autoPlay: boolean;
  wrapClassName?: string;
  gapRepair?: boolean;
}

export interface ShuffleWrapHandle {
  getTimeLine: () => GSAPTimeline | undefined;
  getDOMNode: () => HTMLDivElement;
}

export const ShuffleWrap = forwardRef<ShuffleWrapHandle, ShuffleWrapProps>(
  function ShuffleWrap(props, ref) {
    const {
      orientation = "vertical",
      slicesTotal = 5,
      animation,
      scrollTrigger,
      autoPlay,
      onComplete,
      onReverseComplete,
      wrapClassName,
      gapRepair = false,
      ...reset
    } = props;

    const childrenInnerRefs = useRef<HTMLDivElement[]>([]);
    const childrenRef = useRef<HTMLDivElement>(null);
    const childrenInnerBoxRef = useRef<HTMLDivElement>(null);
    const ctnRef = useRef<HTMLDivElement>(null);
    const timeline = useRef<GSAPTimeline>();

    function setClipPath() {
      childrenInnerRefs.current.forEach((slice, position) => {
        const a1 = (position * 100) / slicesTotal;
        const b1 = (position * 100) / slicesTotal + 100 / slicesTotal;
        gsap.set(slice, {
          clipPath:
            orientation === "vertical"
              ? `polygon(${a1}% 0%, ${b1}% 0%, ${b1}% 100%, ${a1}% 100%)`
              : `polygon(0% ${a1}%, 100% ${a1}%, 100% ${b1}%, 0% ${b1}%)`,
        });
        const isVertical = orientation === "vertical";
        if (gapRepair) {
          gsap.set(slice, { [isVertical ? "left" : "top"]: position * -1 });
        }
      });
    }

    function shuffleAnimation() {
      // const children = childrenRef.current as HTMLDivElement;
      // const childrenInnerBox = childrenInnerBoxRef.current as HTMLDivElement
      const isVertical = orientation === "vertical";
      timeline.current = gsap
        .timeline({
          defaults: animation,
          paused: true,
          onStart: () => {
            // console.log("start");
          },
          onComplete: () => {
            // console.log("Complete");
            if (onComplete) onComplete();
          },
          onReverseComplete: () => {
            // console.log('ReverseComplete') 
            if(onReverseComplete) onReverseComplete()
          }
        })
        .addLabel("start", 0)
        .fromTo(
          childrenRef.current,
          {
            [isVertical ? "yPercent" : "xPercent"]: 100,
            opacity: 0,
          },
          {
            [isVertical ? "yPercent" : "xPercent"]: 0,
            opacity: 1,
          },
          "start"
        )
        .fromTo(
          childrenInnerBoxRef.current,
          {
            // [isVertical ? "yPercent" : "xPercent"]: -100,
            opacity: 0,
          },
          {
            // [isVertical ? "yPercent" : "xPercent"]: 0,
            opacity: 1,
          },
          "start"
        )
        .fromTo(
          childrenInnerRefs.current,
          {
            [isVertical ? "yPercent" : "xPercent"]: (pos: number) =>
              pos % 2 ? gsap.utils.random(-75, -25) : gsap.utils.random(25, 75),
            opacity: 0,
          },
          {
            [isVertical ? "yPercent" : "xPercent"]: 0,
            opacity: 1,
          },
          "start"
        );
    }

    function resetAnimation() {
      const isVertical = orientation === "vertical";
      gsap
        .timeline({
          defaults: { duration: animation?.duration, ease: animation?.ease },
          onStart: () => {
            // console.log("reset");
          },
        })
        .addLabel("reset", 0)
        .fromTo(
          childrenRef.current,
          {
            // [isVertical ? "yPercent" : "xPercent"]: 0,
            opacity: 1,
          },
          {
            // [isVertical ? "yPercent" : "xPercent"]: 100,
            opacity: 0,
          },
          "reset"
        )
        .fromTo(
          childrenInnerBoxRef.current,
          {
            // [isVertical ? "yPercent" : "xPercent"]: -100,
            opacity: 1,
          },
          {
            // [isVertical ? "yPercent" : "xPercent"]: 0,
            opacity: 0,
          },
          "reset"
        );
      // .fromTo(
      //   childrenInnerRefs.current,
      //   {
      //     [isVertical ? "yPercent" : "xPercent"]: 0,
      //     opacity: 1,
      //   },
      //   {
      //     [isVertical ? "yPercent" : "xPercent"]: (pos: number) =>
      //       pos % 2 ? gsap.utils.random(-75, -25) : gsap.utils.random(25, 75),
      //     opacity: 0,
      //   },
      //   "reset"
      // );
    }

    useEffect(() => {
      setClipPath();
    }, []);

    useGsap(() => {
      shuffleAnimation();
    }, []);

    useImperativeHandle(
      ref,
      () => {
        return {
          getTimeLine: () => {
            if (timeline.current) {
              return timeline.current;
            }
          },
          getDOMNode: () => ctnRef.current as HTMLDivElement,
        };
      },
      [timeline.current]
    );

    useGsap(() => {
      if (autoPlay) {
        timeline.current?.play();
      }
    }, []);

    useGsap(() => {
      if (!autoPlay && scrollTrigger) {
        ScrollTrigger.create({
          trigger: ctnRef.current,
          ...scrollTrigger,
          onEnter: (self) => {
            if (scrollTrigger?.onEnter) {
              timeline.current?.play();
              scrollTrigger.onEnter(self);
            } else {
              timeline.current?.play();
            }
          },
        });
      }
    }, []);

    // useGsap(() => {
    //   if (animation?.scrollTrigger) {
    //     ScrollTrigger.create({
    //       trigger: childrenRef.current,
    //       ...animation.scrollTrigger,
    //       onEnter: (self) => {
    //         if (animation?.scrollTrigger?.onEnter) {
    //           shuffleAnimation();
    //           animation.scrollTrigger.onEnter(self);
    //         }
    //       },
    //     });
    //   } else {
    //     shuffleAnimation();
    //   }
    // });

    return (
      <div className={`${styles.ctn} ${wrapClassName}`} ref={ctnRef}>
        <div className={`${styles.children} `} ref={childrenRef}>
          <div {...reset} className={reset.className}>
            {reset.children}
          </div>
        </div>
        <div className={styles.childrenInnerBox} ref={childrenInnerBoxRef}>
          {Array.from({ length: slicesTotal }, (_, index) => (
            <div
              key={index}
              ref={(element: HTMLDivElement) => {
                childrenInnerRefs.current[index] = element;
              }}
              className={`${styles.childrenInner} `}
            >
              <div {...reset} className={reset.className}>
                {reset.children}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
);

export default ShuffleWrap;
